import { motion } from "framer-motion";
import { Mic, ArrowRight } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";
import { SPEAKING } from "@/data/content";

const TILE_STYLES = [
  "bg-white text-obsidian",
  "bg-yellow text-obsidian",
  "bg-coral text-white",
  "bg-white text-obsidian",
  "bg-obsidian text-white border-2 border-white/10",
  "bg-blue text-obsidian",
  "bg-white text-obsidian",
  "bg-yellow text-obsidian",
  "bg-coral text-white",
];

export const Speaking = () => {
  return (
    <section
      id="speaking"
      data-testid="speaking-section"
      className="bg-obsidian text-white py-20 md:py-32 relative overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative">
        <div className="mb-12 md:mb-16 max-w-3xl">
          <span className="font-outfit text-xs uppercase tracking-widest text-yellow">
            Speaking Engagements
          </span>
          <h2 className="font-anton uppercase text-5xl md:text-7xl leading-[0.9] mt-3">
            On Stage <span className="text-blue">At</span>
          </h2>
          <p className="font-outfit font-medium text-white/70 mt-4">
            Sharing what we&apos;ve learned on stage with creators, students, and brand teams 🎤
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {SPEAKING.map((item, i) => {
            const tileClass = item.tileClass || TILE_STYLES[i % TILE_STYLES.length];
            return (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                whileHover={{ y: -4, rotate: i % 2 === 0 ? -1.5 : 1.5 }}
                data-testid={`speaking-tile-${item.key}`}
                className={`rounded-2xl px-5 h-32 md:h-36 flex items-center justify-center text-center shadow-xl ${tileClass}`}
              >
                {item.logo ? (
                  <img
                    src={item.logo}
                    alt={item.name}
                    loading="lazy"
                    className={item.logoClass || "max-h-20 md:max-h-24 w-auto max-w-[85%] object-contain"}
                  />
                ) : (
                  <span className="font-anton uppercase text-xl md:text-2xl leading-tight tracking-tight">
                    {item.name}
                  </span>
                )}
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-14 md:mt-20 bg-blue text-obsidian rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-10 shadow-2xl"
        >
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-obsidian text-white font-outfit font-bold text-[10px] md:text-xs uppercase tracking-wider px-3 py-1.5 rounded-full mb-4">
              <Mic size={14} /> Book Us
            </div>
            <h3 className="font-anton uppercase text-3xl md:text-5xl leading-[0.95]">
              Interested in booking Justin <span className="text-coral">or</span> Producer Rob to speak at your next big event?
            </h3>
          </div>
          <motion.button
            data-testid="speaking-cta"
            onClick={() => scrollToId("contact")}
            whileHover={{ scale: 1.06, rotate: -2 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 14 }}
            className="shrink-0 inline-flex items-center gap-2 font-outfit font-extrabold text-sm md:text-base uppercase tracking-wide bg-obsidian text-white px-8 py-5 rounded-full hover:bg-coral transition-colors duration-200"
          >
            Have Us
            <ArrowRight size={18} />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};
